import Chart from 'react-apexcharts';
import { TChartValue } from 'views/types/volume';
import { formatDollarAmount } from 'utils/formatNumber';
import { Box } from '@mui/material';
import { ApexOptions } from 'apexcharts';

type Props = {
  data: TChartValue[];
  children: React.ReactNode;
  selectedDay: string;
};

const AreaApexChart: React.FC<Props> = ({ data, children }) => {
  const series = [
    {
      data: data?.map((el) => ({
        x: el.timestamp,
        y: el.value,
      })),
    },
  ];

  const maxY = Math.max(...data.map((el) => el.value));

  const options = {
    chart: {
      id: 'area-datetime',
      height: 350,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    stroke: {
      show: true,
      curve: 'smooth' as ApexStroke['curve'],
      lineCap: 'butt' as const,
      width: 2,
      dashArray: 0,
    },

    dataLabels: {
      enabled: false,
    },
    xaxis: {
      type: 'datetime',
      tickPlacement: 'between',
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: '#00000080',
          fontSize: '12px',
          fontFamily: 'Inter',
          fontWeight: '400 !important',
          cssClass: 'apexcharts-xaxis-label',
        },
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM dd',
          day: 'MMM dd',
          hour: 'HH:mm',
          minute: 'HH:mm:ss',
          second: 'HH:mm:ss',
        },
      },
      tooltip: {
        enabled: true,
        offsetY: 10,
        style: {
          colors: '#00000080',
          fontSize: '12px',
          fontFamily: 'Inter',
          fontWeight: '400 !important',
          cssClass: 'apexcharts-xaxistooltip',
        },
      },
      crosshairs: {
        show: true,
        width: 44,
        opacity: 0.9,
        stroke: {
          width: 0,
          dashArray: 0,
        },
        fill: {
          type: 'gradient',
          gradient: {
            colorTo: '#5663f265',
            stops: [0, 100],
            opacityFrom: 0,
            opacityTo: 1,
          },
        },
      },
    },
    markers: {
      size: 0,
      strokeColors: '#5663F2',
      strokeWidth: 5,
      strokeOpacity: 1,
      strokeDashArray: 0,
      fillOpacity: 1,
    },
    yaxis: {
      min: maxY < 0.01 ? 0.01 : undefined,
      decimalsInFloat: 2,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: ['#00000080'],
          fontSize: '12px',
          fontFamily: 'Inter',
          fontWeight: 400,
          cssClass: 'apexcharts-yaxis-label',
        },
      },
    },
    grid: {
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    tooltip: {
      followCursor: false,
      style: {
        fontSize: '12px',
        fontFamily: 'Inter',
      },
      items: {
        display: 'flex',
        gap: '50px',
      },
      marker: {
        show: false,
      },
      x: {
        show: false,
        format: 'dd MMM yyyy',
      },
      y: {
        show: true,
        formatter: function (value: number) {
          return `${formatDollarAmount(value, 2, 2, true)}`;
        },
        fillSeriesColor: false,
        title: {
          formatter: () => '',
        },
      },
    },
    fill: {
      type: 'gradient',
      colors: ['#5663F2'],
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.6,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
  } as ApexOptions;

  return (
    <div className="h-full w-[calc(100%+15px)]">
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          '& .MuiResponsiveChart-container': {
            flexGrow: 1,
          },
          height: 'calc(100% - 48px)',
        }}>
        {children}

        {data && data.length ? (
          <Chart options={options} series={series} type="area" height="100%" />
        ) : (
          <div className="flex dark:text-white items-center justify-center h-[150px]">No data</div>
        )}
      </Box>
    </div>
  );
};

export default AreaApexChart;
